import React, { useCallback, useEffect, useRef, useState } from "react";
import { Extension } from "@tiptap/core";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import HardBreak from "@tiptap/extension-hard-break";
import {
  Table,
  TableRow,
  TableHeader,
  TableCell,
} from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import { Slice } from "@tiptap/pm/model";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import remarkBreaks from "remark-breaks";
import {
  flattenParagraphSiblings,
  flattenToPlainParagraph,
} from "./pasteTransform";
import "./style.css";

const CustomHardBreak = HardBreak.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any, parent: any, index: number) {
          for (let i = index + 1; i < parent.childCount; i++)
            if (parent.child(i).type !== node.type) {
              state.write(state.inTable ? "<br>" : "\n");
              return;
            }
        },
        parse: {},
      },
    };
  },
});

const SingleEnterBreak = Extension.create({
  name: "singleEnterBreak",
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        if (
          editor.isActive("listItem") ||
          editor.isActive("tableCell") ||
          editor.isActive("tableHeader")
        ) {
          return false;
        }

        const { $from } = editor.state.selection;
        if ($from.nodeBefore?.type.name === "hardBreak") {
          return editor
            .chain()
            .deleteRange({ from: $from.pos - 1, to: $from.pos })
            .splitBlock()
            .run();
        }

        return editor.chain().setHardBreak().run();
      },
      "Shift-Enter": ({ editor }) => {
        return editor.chain().createParagraphNear().run();
      },
    };
  },
});

export interface ToolbarButton {
  name: string;
  icon: React.ReactNode;
  tooltip?: string;
  isActive?: (editor: Editor) => boolean;
  action: (editor: Editor) => void;
}

interface Props {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onBlur?: (markdown: string) => void;
  onChange?: (markdown: string) => void;
  onDebouncedChange?: (markdown: string) => void;
  debounceMs?: number;
  toolbarButtons?: ToolbarButton[];
  customToolbarButtons?: ToolbarButton[] /* appends to default buttons if present */;
  className?: string;
  borderColor?: string;
  alwaysEdit?: boolean;
}

const headingLevels = [1, 2, 3, 4] as const;
type HeadingLevel = (typeof headingLevels)[number];

const defaultToolbarButtons: ToolbarButton[] = [
  {
    name: "heading",
    icon: (
      <span className="mdtav2-btn-label">
        H<span className="mdtav2-heading-caret">&#9662;</span>
      </span>
    ),
    tooltip: "Heading",
    isActive: (editor) => editor.isActive("heading"),
    action: () => {},
  },
  {
    name: "bold",
    icon: <span className="mdtav2-btn-label mdtav2-bold">B</span>,
    tooltip: "Bold",
    isActive: (editor) => editor.isActive("bold"),
    action: (editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    name: "italic",
    icon: <span className="mdtav2-btn-label mdtav2-italic">I</span>,
    tooltip: "Italic",
    isActive: (editor) => editor.isActive("italic"),
    action: (editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    name: "divider1",
    icon: null,
    action: () => {},
  },
  {
    name: "bulletList",
    icon: <span className="mdtav2-btn-label">&#8226;</span>,
    tooltip: "Bullet List",
    isActive: (editor) => editor.isActive("bulletList"),
    action: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    name: "orderedList",
    icon: <span className="mdtav2-btn-label">1.</span>,
    tooltip: "Ordered List",
    isActive: (editor) => editor.isActive("orderedList"),
    action: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    name: "indent",
    icon: <span className="mdtav2-btn-label">&rarr;|</span>,
    tooltip: "Indent",
    action: (editor) => editor.chain().focus().sinkListItem("listItem").run(),
  },
  {
    name: "outdent",
    icon: <span className="mdtav2-btn-label">|&larr;</span>,
    tooltip: "Outdent",
    action: (editor) => editor.chain().focus().liftListItem("listItem").run(),
  },
  {
    name: "divider2",
    icon: null,
    action: () => {},
  },
  {
    name: "table",
    icon: <span className="mdtav2-btn-label">#</span>,
    tooltip: "Insert Table",
    action: (editor) =>
      editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run(),
  },
  {
    name: "link",
    icon: <span className="mdtav2-btn-label">&infin;</span>,
    tooltip: "Link",
    isActive: (editor) => editor.isActive("link"),
    action: (editor) => {
      if (editor.isActive("link")) {
        editor.chain().focus().unsetLink().run();
        return;
      }
      const url = window.prompt("Enter URL:");
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
    },
  },
  {
    name: "divider3",
    icon: null,
    action: () => {},
  },
  {
    name: "clearFormatting",
    icon: <span className="mdtav2-btn-label">T&times;</span>,
    tooltip: "Clear Formatting",
    action: (editor) => {
      const flat = flattenToPlainParagraph(
        editor.state.doc.content,
        editor.schema
      );
      editor.chain().setContent(flat.toJSON()).focus("end").run();
    },
  },
];

const HeadingDropdown: React.FC<{ editor: Editor }> = ({ editor }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const isAnyHeadingActive = editor.isActive("heading");

  return (
    <div ref={ref} className="mdtav2-heading-dropdown">
      <button
        type="button"
        className={`mdtav2-toolbar-btn${isAnyHeadingActive ? " active" : ""}`}
        title="Heading"
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
      >
        <span className="mdtav2-btn-label">
          H<span className="mdtav2-heading-caret">&#9662;</span>
        </span>
      </button>
      {open && (
        <div className="mdtav2-heading-menu">
          {headingLevels.map((level) => {
            const active = editor.isActive("heading", { level });
            return (
              <button
                key={level}
                type="button"
                className={`mdtav2-heading-menu-item${active ? " active" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor
                    .chain()
                    .focus()
                    .toggleHeading({ level: level as HeadingLevel })
                    .run();
                  setOpen(false);
                }}
              >
                <span className={`mdtav2-heading-label mdtav2-h${level}`}>
                  H{level}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Toolbar: React.FC<{
  editor: Editor;
  buttons: ToolbarButton[];
}> = ({ editor, buttons }) => (
  <div className="mdtav2-toolbar" role="toolbar">
    {buttons.map((btn) => {
      if (btn.icon === null) {
        return <span key={btn.name} className="mdtav2-toolbar-divider" />;
      }
      if (btn.name === "heading") {
        return <HeadingDropdown key={btn.name} editor={editor} />;
      }
      const active = btn.isActive?.(editor) ?? false;
      return (
        <button
          key={btn.name}
          type="button"
          className={`mdtav2-toolbar-btn${active ? " active" : ""}`}
          title={btn.tooltip}
          onMouseDown={(e) => {
            e.preventDefault();
            btn.action(editor);
          }}
        >
          {btn.icon}
        </button>
      );
    })}
  </div>
);

const MarkdownTextAreaV2: React.FC<Props> = ({
  value,
  disabled,
  placeholder,
  onBlur,
  onChange,
  onDebouncedChange,
  debounceMs = 3000,
  toolbarButtons,
  customToolbarButtons = [],
  className = "",
  borderColor,
  alwaysEdit = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // single source of truth for rendering behavior
  const isEditingEffective = alwaysEdit || isEditing;

  // Local content tracks what was typed so preview always reflects latest edits
  const [localContent, setLocalContent] = useState(value);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestSavedRef = useRef(value);
  const latestContentRef = useRef(value);
  const editorRef = useRef<Editor | null>(null);
  const onDebouncedChangeRef = useRef(onDebouncedChange);
  onDebouncedChangeRef.current = onDebouncedChange;
  const isProgrammaticUpdateRef = useRef(false);

  const buttons = toolbarButtons ?? [
    ...defaultToolbarButtons,
    ...(customToolbarButtons.length > 0
      ? [
          { name: "customDivider", icon: null, action: () => {} },
          ...customToolbarButtons,
        ]
      : []),
  ];

  // Sync from parent when value prop changes (e.g. after API response)
  useEffect(() => {
    latestSavedRef.current = value;
    latestContentRef.current = value;
    setLocalContent(value);
  }, [value]);

  const flushDebouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (onDebouncedChangeRef.current) {
      const md = latestContentRef.current;
      if (md !== latestSavedRef.current) {
        onDebouncedChangeRef.current(md);
        latestSavedRef.current = md;
      }
    }
  }, []);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3, 4] },
          link: { openOnClick: false, autolink: false },
          hardBreak: false,
        }),
        CustomHardBreak,
        SingleEnterBreak,
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        Placeholder.configure({ placeholder: placeholder || "" }),
        Markdown.configure({
          html: false,
          breaks: true,
          transformPastedText: true,
          transformCopiedText: true,
        }),
      ],
      content: localContent || "",
      editable: !disabled,
      editorProps: {
        transformPasted(slice, view) {
          const newContent = flattenParagraphSiblings(
            slice.content,
            view.state.schema
          );
          return new Slice(newContent, slice.openStart, slice.openEnd);
        },
      },
      onUpdate: ({ editor: ed }) => {
        let md: string;
        try {
          md = (ed.storage as any).markdown?.getMarkdown() ?? "";
        } catch {
          // tiptap-markdown can fail on intermediate editor states (e.g. mid-format)
          return;
        }
        latestContentRef.current = md;
        setLocalContent(md);

        if (onChange && !isProgrammaticUpdateRef.current) onChange(md);

        if (onDebouncedChangeRef.current && md !== latestSavedRef.current) {
          if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            // Read from ref to always get the latest content
            const latest = latestContentRef.current;
            if (
              latest !== latestSavedRef.current &&
              onDebouncedChangeRef.current
            ) {
              onDebouncedChangeRef.current(latest);
              latestSavedRef.current = latest;
            }
          }, debounceMs);
        }
      },
    },
    [isEditingEffective]
  );

  // Keep ref in sync so flushDebouncedSave can access it
  useEffect(() => {
    editorRef.current = editor ?? null;
  }, [editor]);

  // When editor is already mounted (alwaysEdit), push updated value from parent into editor
  useEffect(() => {
    const ed = editorRef.current;
    if (!ed || !alwaysEdit) return;
    const current = (ed.storage as any).markdown?.getMarkdown() ?? "";
    if (value !== current) {
      isProgrammaticUpdateRef.current = true;
      ed.commands.setContent(value || "");
      isProgrammaticUpdateRef.current = false;
    }
  }, [value, alwaysEdit]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        flushDebouncedSave();
      }
    };
  }, [flushDebouncedSave]);

  // Handle Tab to move to next focusable element
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !isEditingEffective) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      e.preventDefault();
      e.stopPropagation();

      const focusable = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[contenteditable="true"], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);

      const current = focusable.findIndex((el) => wrapper.contains(el));
      if (current === -1) return;

      const next = e.shiftKey ? focusable[current - 1] : focusable[current + 1];
      next?.focus();
    };

    wrapper.addEventListener("keydown", handleKeyDown, true);
    return () => wrapper.removeEventListener("keydown", handleKeyDown, true);
  }, [isEditingEffective]);

  // Deactivate editor when focus leaves the wrapper entirely
  useEffect(() => {
    if (!isEditingEffective) return;

    const handleFocusIn = (e: FocusEvent) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const target = e.target as Node;
      if (!wrapper.contains(target)) {
        // Focus moved outside this editor — flush and deactivate
        if (onDebouncedChangeRef.current) {
          flushDebouncedSave();
        } else if (onBlur) {
          const md = latestContentRef.current;
          if (md !== value) onBlur(md);
        }
        // Never exit editing if alwaysEdit is true
        if (!alwaysEdit) setIsEditing(false);
      }
    };

    // Use setTimeout so this listener is added after the current focus settles
    const timer = setTimeout(() => {
      document.addEventListener("focusin", handleFocusIn);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [
    isEditingEffective,
    onBlur,
    onDebouncedChange,
    flushDebouncedSave,
    value,
    alwaysEdit,
  ]);

  // Auto-focus the editor when it mounts
  useEffect(() => {
    if (isEditingEffective && editor && !editor.isFocused) {
      requestAnimationFrame(() => editor.commands.focus());
    }
  }, [isEditingEffective, editor]);

  if (disabled) {
    return (
      <div
        className={`mdtav2 ${className} mdtav2-disabled`}
        style={borderColor ? { borderColor } : undefined}
      >
        <ReactMarkdown remarkPlugins={[remarkBreaks]}>{value}</ReactMarkdown>
      </div>
    );
  }

  // preview ONLY allowed when NOT alwaysEdit
  if (!isEditingEffective) {
    return (
      <div
        className={`mdtav2 ${className} mdtav2-preview`}
        style={borderColor ? { borderColor } : undefined}
        onClick={() => setIsEditing(true)}
        role="button"
        tabIndex={0}
        onFocus={() => setIsEditing(true)}
      >
        {localContent ? (
          <ReactMarkdown remarkPlugins={[remarkBreaks]}>
            {localContent}
          </ReactMarkdown>
        ) : (
          <span className="mdtav2-placeholder">{placeholder}</span>
        )}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`mdtav2 ${className} mdtav2-editing`}
      style={borderColor ? { borderColor } : undefined}
    >
      {editor && <Toolbar editor={editor} buttons={buttons} />}
      <EditorContent editor={editor} className="mdtav2-content" />
    </div>
  );
};

export default MarkdownTextAreaV2;
